import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { ROUTES } from '@/common';
import { DEFAULT_DATE_FORMAT, NoteTypes } from '@/common/constants.ts';
import { OptionKey } from '@/common/types';
import { useHistory } from '@/modules/core/hooks';
import { useHasAccess } from '@/modules/core/hooks';
import useHealthPlanSpendTrackerStore from '@/modules/deductibles/HealthPlanDetails/stores/useHealthPlanSpendTracker.store.ts';
import { getCoverageLevel } from '@/modules/deductibles/SpendTracker/hooks/getCoverageLevel.ts';
import useDeductiblesStore from '@/modules/deductibles/stores/useDeductibles.store.ts';
import useGetNotesQuery from '@/modules/employee/Employee/queries/useGetNotes.query';

import { DeductibleUser } from './useDependents';
import { useDeductibles, useDependents, useGetHealthPlanYears } from './';

const REQUIRED_PERMISSIONS = [{ permission: 'VIEW_HEALTH_PLAN' }, { permission: 'VIEW_PROFILE' }];

interface UseSpendTrackerLogicProps {
  employeeUser: DeductibleUser;
  isIndividual?: boolean;
  defaultHealthPlanYearId?: string;
  onHealthPlanNameChange?: (healthPlanName: string) => void;
  onHealthPlanIdChange?: (healthPlanId: string) => void;
  onHealthPlanCoverageLevelChange?: (coverageLevel: string) => void;
}

const useSpendTrackerLogic = ({
  employeeUser,
  isIndividual,
  defaultHealthPlanYearId,
  onHealthPlanNameChange,
  onHealthPlanIdChange,
  onHealthPlanCoverageLevelChange,
}: UseSpendTrackerLogicProps) => {
  const history = useHistory();
  const [showNotes, setShowNotes] = useState(false);
  const [selectedHealthPlanYearId, setHealthPlanYearId] = useState<OptionKey>();
  const hasAccess = useHasAccess(REQUIRED_PERMISSIONS);
  const handleSetState = useHealthPlanSpendTrackerStore((state) => state.setState);
  const userId = useDeductiblesStore((state) => state.state.userId);
  const {
    formattedData,
    healthPlanYearOptions,
    currentHealthPlanYear,
    isLoading: isPlanYearsLoading,
  } = useGetHealthPlanYears(employeeUser?.id);

  const selectedPlanYear = useMemo(
    () => formattedData?.find((item) => item.id == selectedHealthPlanYearId),
    [formattedData, selectedHealthPlanYearId]
  );

  const {
    isDeductiblesLoading,
    chartValues,
    total,
    chartTiersValues,
    remaining,
    isLoading,
    individualTotal,
    individualTotalAccumulatedValues,
    individualAccumulatedValues,
    healthPlanId,
    healthPolicyId,
    healthPlanName,
    formattedFamilyDeductibles,
    familyAccumulatedValue,
    formattedTiersData,
    isEmbeddedOnly,
  } = useDeductibles({
    focusedUserId: '',
    isIndividual,
    employeeId: employeeUser?.id,
    serviceOn: selectedPlanYear ? dayjs(selectedPlanYear?.validTo).format(DEFAULT_DATE_FORMAT) : undefined,
    selectedHealthPlanId: selectedPlanYear?.healthPlanId,
    userId,
  });

  const users = useDependents({
    userId: employeeUser?.id || userId,
    employeeUser,
    isIndividual,
  });

  const { notes } = useGetNotesQuery(NoteTypes.HEALTH_PLAN_NOTES, healthPlanId);
  const coverageLevel = useMemo(
    () => getCoverageLevel(formattedFamilyDeductibles?.coverageLevel),
    [formattedFamilyDeductibles]
  );
  const handleShowNotes = useCallback(() => {
    setShowNotes(!showNotes);
  }, [showNotes]);

  const handleOpenTrackerDetails = useCallback(() => {
    history.push(
      ROUTES.HEALTH_PLAN_SPENDS_DETAILS(employeeUser?.id, healthPlanId, selectedHealthPlanYearId as string)
    );
  }, [history, employeeUser?.id, healthPlanId, selectedHealthPlanYearId]);

  useEffect(() => {
    if (onHealthPlanNameChange && healthPlanName) {
      onHealthPlanNameChange(healthPlanName);
    }
  }, [healthPlanName, onHealthPlanNameChange]);

  useEffect(() => {
    if (onHealthPlanIdChange && healthPlanId) {
      onHealthPlanIdChange(healthPlanId);
    }
  }, [healthPlanId, onHealthPlanIdChange]);

  useEffect(() => {
    if (onHealthPlanCoverageLevelChange && coverageLevel) {
      onHealthPlanCoverageLevelChange(coverageLevel);
    }
  }, [coverageLevel, onHealthPlanCoverageLevelChange]);

  useEffect(() => {
    if (defaultHealthPlanYearId) {
      setHealthPlanYearId(defaultHealthPlanYearId);
    } else if (currentHealthPlanYear?.id) {
      setHealthPlanYearId(currentHealthPlanYear?.id);
    }
  }, [currentHealthPlanYear?.id, defaultHealthPlanYearId]);

  useEffect(() => {
    if (coverageLevel) {
      handleSetState({
        healthPlanId,
        coverageLevel,
      });
    }
  }, [coverageLevel, handleSetState, healthPlanId]);

  return {
    formattedData,
    healthPolicyId,
    showNotes,
    setShowNotes,
    selectedHealthPlanYearId,
    setHealthPlanYearId,
    hasAccess,
    healthPlanYearOptions,
    isPlanYearsLoading,
    handleShowNotes,
    handleOpenTrackerDetails,
    chartValues,
    chartTiersValues,
    remaining,
    total,
    isLoading,
    notes,
    users,
    coverageLevelLabel: formattedFamilyDeductibles?.coverageLevel,
    individualTotal,
    individualTotalAccumulatedValues,
    individualAccumulatedValues,
    familyAccumulatedValue,
    formattedTiersData,
    isDeductiblesLoading,
    isEmbeddedOnly,
  };
};

export default useSpendTrackerLogic;
